import { Link } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { avatarStyle, initialsOf } from '@/features/crm/lib/avatar';
import { MultiSelect } from '@/features/crm/components/MultiSelect';
import { ListActions } from '@/features/crm/components/ListActions';
import {
  SavedSegmentBar,
  type SavedSegment,
} from '@/features/crm/components/SavedSegmentBar';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { accountCodesCell } from '@/features/crm/lib/account-codes';

interface Customer {
  customer_id: string;
  name: string;
  /** The ONE account number, or null — see crm/lib/account-codes. */
  account_number: string | null;
  account_codes?: string[];
  domain: string | null;
  status: string | null;
  cust_ae: string | null;
  cust_csm: string | null;
  cust_ops_mgr: string | null;
  industry: string | null;
  size: string | null;
  city: string | null;
  state: string | null;
  total_spend_12m: number | null;
  travelers_12m: number | null;
  btp_enabled_hint: boolean | null;
  health_band: string | null;
  health_score: number | null;
  key_contact: string | null;
  key_contact_email: string | null;
}

type Filters = {
  // Book-of-business filters are multi-select so a Segments user can build
  // a list across a team (e.g., "all accounts for Sarah + Maria"), not just
  // one teammate at a time.
  aes: string[];
  csms: string[];
  opsMgrs: string[];
  industry: string;
  size: string;
  status: string;
  btp: string;
  health: string;
  obt: string;
  hasEmail: string;
  minSpend: string;
};

const EMPTY: Filters = {
  aes: [],
  csms: [],
  opsMgrs: [],
  industry: '',
  size: '',
  status: '',
  btp: '',
  health: '',
  obt: '',
  hasEmail: '',
  minSpend: '',
};

function countActiveSegmentFilters(f: Filters): number {
  let n = 0;
  if (f.aes.length) n++;
  if (f.csms.length) n++;
  if (f.opsMgrs.length) n++;
  if (f.industry) n++;
  if (f.size) n++;
  if (f.status) n++;
  if (f.btp) n++;
  if (f.health) n++;
  if (f.obt) n++;
  if (f.hasEmail) n++;
  if (f.minSpend) n++;
  return n;
}

export default function SegmentsPage() {
  const { can } = useMe();
  // write:segment — deliberately NOT held by `viewer`, whose whole reason to
  // exist is look-but-don't-touch. A read-only role can still open and re-run
  // a shared segment.
  const canWriteSegment = can?.('write:segment') ?? false;

  const [all, setAll] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [draft, setDraft] = useState<Filters>(EMPTY);
  const [applied, setApplied] = useState<Filters | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiFetch<{ customers: Customer[] }>('/api/customers?limit=5000')
      .then((d) => {
        if (!cancelled) setAll(d.customers);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const facets = useMemo(() => {
    const a = new Set<string>(),
      c = new Set<string>(),
      o = new Set<string>(),
      i = new Set<string>(),
      s = new Set<string>();
    for (const x of all) {
      if (x.cust_ae) a.add(x.cust_ae);
      if (x.cust_csm) c.add(x.cust_csm);
      if (x.cust_ops_mgr) o.add(x.cust_ops_mgr);
      if (x.industry) i.add(x.industry);
      if (x.size) s.add(x.size);
    }
    return {
      aes: Array.from(a).sort(),
      csms: Array.from(c).sort(),
      ops: Array.from(o).sort(),
      industries: Array.from(i).sort(),
      sizes: [
        'Micro',
        'Small',
        'Mid Market',
        'Large Market',
        'Enterprise',
      ].filter((sz) => s.has(sz)),
    };
  }, [all]);

  const results = useMemo(() => {
    if (!applied) return [];
    let r = all;
    if (applied.aes.length)
      r = r.filter((x) => x.cust_ae && applied.aes.includes(x.cust_ae));
    if (applied.csms.length)
      r = r.filter((x) => x.cust_csm && applied.csms.includes(x.cust_csm));
    if (applied.opsMgrs.length)
      r = r.filter(
        (x) => x.cust_ops_mgr && applied.opsMgrs.includes(x.cust_ops_mgr),
      );
    if (applied.industry) r = r.filter((x) => x.industry === applied.industry);
    if (applied.size) r = r.filter((x) => x.size === applied.size);
    if (applied.status) r = r.filter((x) => x.status === applied.status);
    if (applied.btp === 'yes') r = r.filter((x) => x.btp_enabled_hint === true);
    if (applied.btp === 'no') r = r.filter((x) => !x.btp_enabled_hint);
    if (applied.health) r = r.filter((x) => x.health_band === applied.health);
    if (applied.hasEmail === 'yes')
      r = r.filter((x) => Boolean(x.key_contact_email));
    if (applied.hasEmail === 'no') r = r.filter((x) => !x.key_contact_email);
    if (applied.minSpend) {
      const min = parseFloat(applied.minSpend.replace(/[^0-9.]/g, ''));
      if (isFinite(min)) r = r.filter((x) => (x.total_spend_12m ?? 0) >= min);
    }
    return r.sort(
      (a, b) => (b.total_spend_12m ?? 0) - (a.total_spend_12m ?? 0),
    );
  }, [applied, all]);

  const activeCount = countActiveSegmentFilters(draft);
  const totalSpend = results.reduce((s, r) => s + (r.total_spend_12m ?? 0), 0);
  const totalTravelers = results.reduce(
    (s, r) => s + (r.travelers_12m ?? 0),
    0,
  );
  const withEmail = results.filter((r) => r.key_contact_email).length;

  /**
   * THE ACCOUNT-LEVEL CSV. Customers only — no contact names, no emails.
   *
   * It used to carry `Key Contact` and `Email` columns, and there was a second
   * "Email list" button that built a CSV of `key_contact_email` in the browser
   * off /api/customers. /api/customers has no consent model, so both downloads
   * shipped people who had opted out, whose address had bounced, who had left
   * the company, or whose record HubSpot had merged — and nothing recorded that
   * the pull happened.
   *
   * Contact data now comes only from <ListActions/>, which asks
   * /api/contacts — the consent-aware, audited path the retired Contacts page
   * used. The two contact columns are gone from here rather than being filtered
   * client-side, because a browser cannot apply suppression it was never sent
   * and cannot write the audit row either.
   */
  const exportCsv = () => {
    if (results.length === 0) return;
    const cols = [
      'Customer',
      'Customer ID',
      'Account #',
      'Account Codes',
      'Status',
      'AE',
      'CSM',
      'Industry',
      'Size',
      '12m Spend',
      'Travelers',
      'Hotel Supplier',
      'Health',
    ];
    const lines = [cols.join(',')];
    for (const r of results) {
      lines.push(
        [
          csv(r.name),
          r.customer_id,
          csv(r.account_number ?? ''),
          csv(accountCodesCell(r.account_codes)),
          r.status || '',
          csv(r.cust_ae || ''),
          csv(r.cust_csm || ''),
          csv(r.industry || ''),
          r.size || '',
          r.total_spend_12m || 0,
          r.travelers_12m || 0,
          r.btp_enabled_hint ? 'Yes' : 'No',
          r.health_band || '',
        ].join(','),
      );
    }
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `segment-accounts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Segments & Lists</h1>
        <p className="page-intro">
          Build a customer slice with chip filters, see the dollar and traveler
          shape of it, then save it so you can come back and re-run it. Account
          data exports straight from here; <strong>contact lists</strong> are
          built from the Contact records at those accounts, so people who have
          opted out or bounced are excluded and every pull is recorded.
        </p>
      </div>

      <SavedSegmentBar
        canWrite={canWriteSegment}
        currentFilters={draft as unknown as Record<string, unknown>}
        kind="customers"
        onLoad={(filters, seg: SavedSegment) => {
          // Reopening applies the stored CRITERIA and re-runs them, so the
          // counts below are today's rather than the ones from when it was
          // saved. See SavedSegmentBar's header for why nothing is frozen.
          const next = { ...EMPTY, ...(filters as Partial<Filters>) };
          setDraft(next);
          setApplied(next);
          void seg;
        }}
      />

      <div className="section">
        <div className="section-header">
          <span className="section-label">Build segment</span>
          <span className="section-meta">
            {loading
              ? 'Loading…'
              : `${all.length.toLocaleString()} customers available`}
          </span>
        </div>
        <div className="section-body">
          {error && <div className="banner banner-error">{error}</div>}

          <div className="form-grid" style={{ marginBottom: 10 }}>
            <Field label="Account Executive (multi)">
              <MultiSelect
                value={draft.aes}
                onChange={(v) => setDraft({ ...draft, aes: v })}
                options={facets.aes}
                placeholder="Any AE"
              />
            </Field>
            <Field label="CSM (multi)">
              <MultiSelect
                value={draft.csms}
                onChange={(v) => setDraft({ ...draft, csms: v })}
                options={facets.csms}
                placeholder="Any CSM"
              />
            </Field>
            <Field label="Ops Manager (multi)">
              <MultiSelect
                value={draft.opsMgrs}
                onChange={(v) => setDraft({ ...draft, opsMgrs: v })}
                options={facets.ops}
                placeholder="Any Ops Mgr"
              />
            </Field>
            <Field label="Industry">
              <FacetSelect
                value={draft.industry}
                onChange={(v) => setDraft({ ...draft, industry: v })}
                options={facets.industries}
                placeholder="Any industry"
              />
            </Field>

            <Field label="Size">
              <FacetSelect
                value={draft.size}
                onChange={(v) => setDraft({ ...draft, size: v })}
                options={facets.sizes}
                placeholder="Any size"
              />
            </Field>
            <Field label="Status">
              <FacetSelect
                value={draft.status}
                onChange={(v) => setDraft({ ...draft, status: v })}
                options={['Active', 'Inactive']}
                placeholder="Any status"
              />
            </Field>
            <Field label="Hotel Supplier">
              <FacetSelect
                value={draft.btp}
                onChange={(v) => setDraft({ ...draft, btp: v })}
                options={[
                  { value: 'yes', label: 'Enrolled' },
                  { value: 'no', label: 'Not enrolled' },
                ]}
                placeholder="Any enrollment status"
              />
            </Field>
            <Field label="Health band">
              <FacetSelect
                value={draft.health}
                onChange={(v) => setDraft({ ...draft, health: v })}
                options={['Growing', 'Stable', 'At Risk', 'Critical']}
                placeholder="Any health band"
              />
            </Field>

            <Field label="Has key-contact email">
              <FacetSelect
                value={draft.hasEmail}
                onChange={(v) => setDraft({ ...draft, hasEmail: v })}
                options={[
                  { value: 'yes', label: 'With email' },
                  { value: 'no', label: 'Without email' },
                ]}
                placeholder="Either"
              />
            </Field>
            <Field label="Min 12m spend ($)">
              <input
                type="number"
                step="10000"
                value={draft.minSpend}
                onChange={(e) =>
                  setDraft({ ...draft, minSpend: e.target.value })
                }
                placeholder="e.g. 100000"
                className="input"
              />
            </Field>
            <div
              style={{
                gridColumn: 'span 2',
                display: 'flex',
                gap: 8,
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
              }}
            >
              <button
                className="btn btn-outline"
                onClick={() => {
                  setDraft(EMPTY);
                  setApplied(null);
                }}
              >
                Reset
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setApplied(draft)}
                disabled={activeCount === 0}
              >
                Run segment{' '}
                {activeCount > 0 && (
                  <span style={{ opacity: 0.7 }}>· {activeCount} filters</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {applied && (
        <>
          <div className="kpi-row kpi-row-4">
            <div className="kpi-card kpi-highlight">
              <span className="kpi-label">Segment size</span>
              <span className="kpi-value">
                {results.length.toLocaleString()}
              </span>
              <span className="kpi-sub">customers matching filters</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">12m Spend</span>
              <span className="kpi-value kpi-value-sm">
                {fmtMoneyShort(totalSpend)}
              </span>
              <span className="kpi-sub">total program spend</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Travelers</span>
              <span className="kpi-value kpi-value-sm">
                {totalTravelers.toLocaleString()}
              </span>
              <span className="kpi-sub">12-month uniques</span>
            </div>
            <div className="kpi-card kpi-green">
              <span className="kpi-label">Reachable</span>
              <span className="kpi-value kpi-value-sm">
                {withEmail.toLocaleString()}
              </span>
              <span className="kpi-sub">have a key-contact email</span>
            </div>
          </div>

          <div className="section">
            <div className="section-header">
              <span className="section-label">Segment</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-outline"
                  disabled={results.length === 0}
                  onClick={exportCsv}
                  style={{
                    background: 'rgba(255,254,252,0.06)',
                    borderColor: 'rgba(255,254,252,0.18)',
                    color: 'var(--text-white)',
                  }}
                  type="button"
                >
                  Export accounts CSV
                </button>
              </div>
            </div>
            <div className="section-body">
              {/* CONTACT LISTS COME FROM HERE, not from the account CSV above.
                  This is the consent-aware, audited path — the one the retired
                  Contacts page used. Building it off the customer rows (which
                  is what the old "Email list" button did) shipped opted-out and
                  bounced addresses with no record of the pull. */}
              <ListActions
                customerIds={results.map((r) => r.customer_id)}
                disabledReason="Run a segment above, then build a contact list from it."
              />

              {results.length === 0 ? (
                <div className="empty-state">
                  <div className="glyph">⌕</div>
                  <div className="empty-head">
                    No customers match this segment
                  </div>
                  <div className="empty-sub">
                    Loosen one of the filters above and run again. Most
                    segmentation work starts with one strict facet (e.g.
                    industry) and adds finer constraints later.
                  </div>
                </div>
              ) : (
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>AE / CSM</th>
                        <th>Industry / Size</th>
                        <th className="right">12m Spend</th>
                        <th className="right">Travelers</th>
                        <th>Key Contact</th>
                        <th>Health</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((c) => (
                        <tr key={c.customer_id}>
                          <td>
                            <Link
                              to={`/crm/customers/${encodeURIComponent(c.customer_id)}`}
                              className="cust-cell"
                              style={{ textDecoration: 'none' }}
                            >
                              <span style={avatarStyle(c.name, 32)}>
                                {initialsOf(c.name)}
                              </span>
                              <span className="cust-cell-body">
                                <span className="cust-cell-name">{c.name}</span>
                                <span className="cust-cell-meta">
                                  {c.customer_id}
                                </span>
                              </span>
                            </Link>
                          </td>
                          <td>
                            <div style={{ fontSize: 'var(--fs-base)' }}>
                              {c.cust_ae || '—'}
                            </div>
                            <div
                              style={{
                                fontSize: 'var(--fs-xs)',
                                color: 'var(--text-muted)',
                              }}
                            >
                              {c.cust_csm || '—'}
                            </div>
                          </td>
                          <td>
                            <div style={{ fontSize: 'var(--fs-base)' }}>
                              {c.industry || '—'}
                            </div>
                            <div
                              style={{
                                fontSize: 'var(--fs-xs)',
                                color: 'var(--text-muted)',
                              }}
                            >
                              {c.size || '—'}
                            </div>
                          </td>
                          <td className="right">
                            {fmtMoneyShort(c.total_spend_12m ?? 0)}
                          </td>
                          <td className="right">
                            {(c.travelers_12m ?? 0).toLocaleString()}
                          </td>
                          <td>
                            {c.key_contact ? (
                              <>
                                <div style={{ fontSize: 'var(--fs-base)' }}>
                                  {c.key_contact}
                                </div>
                                {c.key_contact_email && (
                                  <a
                                    href={`mailto:${c.key_contact_email}`}
                                    className="link"
                                    style={{ fontSize: 'var(--fs-xs)' }}
                                  >
                                    {c.key_contact_email}
                                  </a>
                                )}
                              </>
                            ) : (
                              <span style={{ color: 'var(--text-faint)' }}>
                                —
                              </span>
                            )}
                          </td>
                          <td>
                            {c.health_band ? (
                              <span
                                className={`health-pill ${healthSlug(c.health_band)}`}
                              >
                                {c.health_band}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {!applied && !loading && (
        <div className="empty-state" style={{ marginTop: 18 }}>
          <div className="glyph">⌘</div>
          <div className="empty-head">Build a segment to start</div>
          <div className="empty-sub">
            Pick one or more filters above and click{' '}
            <strong>Run segment</strong>. Common uses: health-band lists for QBR
            prep, supplier-enrolled by AE for renewal planning, "Mid Market in
            Education with email" for a marketing campaign.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── small components ────────────────────────────────────────────────

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}

type FacetOption = string | { value: string; label: string };

function FacetSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: FacetOption[];
  placeholder: string;
}) {
  return (
    <select
      value={value}
      data-active={value ? '1' : '0'}
      onChange={(e) => onChange(e.target.value)}
      className="select"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const v = typeof o === 'string' ? o : o.value;
        const l = typeof o === 'string' ? o : o.label;
        return (
          <option key={v} value={v}>
            {l}
          </option>
        );
      })}
    </select>
  );
}

function fmtMoneyShort(v: number | null): string {
  if (v == null || !isFinite(v) || v === 0) return '$0';
  if (v >= 1_000_000_000) return '$' + (v / 1_000_000_000).toFixed(2) + 'B';
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(2) + 'M';
  if (v >= 10_000) return '$' + (v / 1_000).toFixed(0) + 'k';
  return '$' + v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function csv(v: string | null | undefined): string {
  const s = String(v ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function healthSlug(b: string): string {
  return b.toLowerCase().replace(/\s+/g, '_');
}
